import { Component, OnInit} from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpService } from '@core/services/http.service';
import { forkJoin, Subject } from 'rxjs';
import { ModalDismissReasons, NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RegistrationService } from '../registration.service';
import { ModalComponent } from '@app/shared/custom-component/modal/modal.component';
import { modalButtonTitles, ModalConfig, modalContent, modalHeading } from '@app/shared/constants/modal.model';
import { MatDialog } from '@angular/material/dialog';
import { ContentManagementModalComponent } from '@app/systemconfiguration/content-management/modal/content-management-modal.component';
import { finalize, take, takeUntil } from 'rxjs/operators';
import { LocalService } from '@app/@core/services/local.service';
import { CommonModule, NgFor } from '@angular/common';
import { AlertService } from '@app/@core/services/alert.service';


@Component({
  selector: 'app-epp-registration',
  templateUrl: './epp-registration.component.html',
  styleUrls: ['./epp-registration.component.scss'],
  standalone: true,
   imports: [ReactiveFormsModule, FormsModule, CommonModule]
})
export class EppRegistrationComponent implements OnInit {
  customModal: NgbModalRef;
  eppRegistrationForm: FormGroup;
  districtList: any[] = [];
  eppProviderList: any[] = [];
  errorMessages: any[] = [];
  toastMessages: any[] = [];
  genericMessages: any[] = [];
  placeholderMessages: any[] = [];
  userAgreementContent: string = '';
  modalRef: NgbModalRef;
  closeResult = '';
  currentDate: string = new Date().toISOString().split('T')[0];
  errorMessage: any = {};
  toastMessage: any = {};
  genericMessage: any = {};
  phoneError: boolean = false;
  phoneExists: boolean = false;
  placeholderMessage: any = {};
  showModal: boolean = false;
  isPhoneFocused: boolean = false;
  private destroy$ = new Subject<void>();
  emailFromUrl: string = '';

  get currentToastMessage(): string {
    if (this.phoneError) {
      return this.toastMessage['T0004'] || 'The phone number entered is already on file.';
    }
    return '';
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly httpService: HttpService,
    private readonly ngModal: NgbModal,
    private readonly localstorage: LocalService,
    private readonly registrationService: RegistrationService,
    private readonly route: ActivatedRoute,
    private alertService: AlertService,
    private readonly matDialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.emailFromUrl = params['email'] || '';
        this.createForm();
        if (this.emailFromUrl) {
          this.registrationService.getUserDetailsByEmail(this.emailFromUrl)
            .pipe(takeUntil(this.destroy$))
            .subscribe((user: any) => {
              if (user?.data?.data) {
                const providerKey = user.data.data.additional_fields?.provider || '';
                this.eppRegistrationForm.patchValue({
                  firstName: user.data.data.firstname || '',
                  lastName: user.data.data.lastname || '',
                  organization: user.data.data.organization || '',
                  eppProvider: providerKey
                });
                ['firstName', 'lastName'].forEach(field => {
                  const control = this.eppRegistrationForm.get(field);
                  if (control?.invalid) {
                    control.markAsTouched();
                  }
                });
              }
              this.loadDropdownData();
            }, () => {
              this.loadDropdownData();
            });
        } else {
          this.loadDropdownData();
        }
      });
  }

  private async loadDropdownData() {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=DISTRICT'),
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=EPP-PROVIDER'),
      this.registrationService.getUserAgreementContent(500, '3', 'EPP'),
    ]).subscribe({
      next: (item: any) => {
        this.districtList = item[0].data.sort((a, b) => a.value.localeCompare(b.value));
        this.eppProviderList = item[5]?.data || [];

        // Map provider key from user details to EPP provider lookup and patch address fields
        const providerKey = this.eppRegistrationForm.get('eppProvider')?.value;
        if (providerKey && this.eppProviderList.length > 0) {
          const matchedProvider = this.eppProviderList.find(p => String(p.key) === String(providerKey));
          if (matchedProvider) {
            this.eppRegistrationForm.patchValue({ eppProvider: matchedProvider.key });
            if (matchedProvider.optionaldata) {
              this.eppRegistrationForm.patchValue({
                streetAddress: matchedProvider.optionaldata['street address'] || '',
                city: matchedProvider.optionaldata['city'] || '',
                state: matchedProvider.optionaldata['state'] || '',
                zipCode: matchedProvider.optionaldata['zipcode'] || ''
              });
            }
          }
        }

        this.errorMessages = item[1].data;
        this.toastMessages = item[2].data;
        this.genericMessages = item[3].data;
        this.placeholderMessages = item[4].data;

        this.errorMessage = {};
        this.errorMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.errorMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('errorMessages', JSON.stringify(this.errorMessages));

        this.toastMessage = {};
        this.toastMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.toastMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('toastMessages', JSON.stringify(this.toastMessages));

        this.genericMessage = {};
        this.genericMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.genericMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('genericMessages', JSON.stringify(this.genericMessages));

        this.placeholderMessage = {};
        this.placeholderMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.placeholderMessage[message.key] = message.value;
          }
        });
        window.localStorage.setItem('placeholderMessages', JSON.stringify(this.placeholderMessages));

        // Store user agreement content from lookup
        if (item[6]?.data?.length > 0) {
          const optionalData = item[6].data[0].optionaldata;
          if (optionalData) {
            try {
              const parsed = typeof optionalData === 'string' ? JSON.parse(optionalData) : optionalData;
              this.userAgreementContent = parsed?.content || '';
            } catch {
              this.userAgreementContent = optionalData;
            }
          }
        }

      },
      error: error => {
        console.error('Error loading dropdown data:', error);
      }
    });
  }

  createForm(user?: any) {
    this.eppRegistrationForm = this.fb.group({
      email: [this.emailFromUrl, ],
      firstName: [
        user?.firstname || '',
        [
          Validators.required,
          Validators.pattern("^[A-Za-z]+([-' ][A-Za-z]+)*\\s*$"),
          Validators.minLength(2),
          Validators.maxLength(30)
        ]
      ],
      lastName: [
        user?.lastname || '',
        [
          Validators.required,
          Validators.pattern("^[A-Za-z]+([-' ][A-Za-z]+)*\\s*$"),
          Validators.minLength(2),
          Validators.maxLength(30)
        ]
      ],
      department: [
        '',
        [
          Validators.pattern("^[A-Za-z][A-Za-z\\s.,\\-&'()\\/]*$"),
          Validators.minLength(2),
          Validators.maxLength(60)
        ]
      ],
      title: [
        '',
        [
          Validators.pattern("^[A-Za-z][A-Za-z\\s.,\\-&'()\\/]*$"),
          Validators.minLength(2),
          Validators.maxLength(60)
        ]
      ],
      eppProvider: [{value: '', disabled: true}],
      streetAddress: [
        {value: '', disabled: true},
        [
          Validators.required,
          Validators.pattern("^[A-Za-z0-9][A-Za-z0-9\\s.,\\-'/#]*$"),
          Validators.minLength(3),
          Validators.maxLength(60)
        ]
      ],
      zipCode: [
        {value: '', disabled: true},
        [
          Validators.required,
          Validators.pattern('^\\d{5}(|-\\d{4})$'),
          Validators.minLength(5),
          Validators.maxLength(10)
        ]
      ],
      city: [{value: '', disabled: true}],
      state: [{value: '', disabled: true}],
      phoneNumber: [
        '',
        [
          Validators.pattern('^(\\+1 )?\\(\\d{3}\\) \\d{3}-\\d{4}$'),
          Validators.maxLength(17)
        ]
      ],
      extension: [
        '',
        [
          Validators.pattern('^\\d{1,10}$'),
          Validators.minLength(2),
          Validators.maxLength(10)
        ]
      ],
      terms: [false, Validators.requiredTrue],
      consent:[false, Validators.requiredTrue]
    }, { validators: this.cityStateRequiredIfZipValid });
  }

  // Input handlers
  onFirstNameInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^A-Za-z\-' ]/g, '');
    value = value.substring(0, 30);
    value = this.preventBackToBackChars(value);
    this.eppRegistrationForm.get('firstName')?.setValue(this.toTitleCase(this.normalizeSpaces(value)), { emitEvent: false });
  }

  onLastNameInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^A-Za-z\-' ]/g, '');
    value = value.substring(0, 30);
    value = this.preventBackToBackChars(value);
    this.eppRegistrationForm.get('lastName')?.setValue(this.toTitleCase(this.normalizeSpaces(value)), { emitEvent: false });
  }

  private toTitleCase(text: string): string {
    if (!text) return '';
    return text.toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  }

  ondepartmentInput(event: any) {
    let value = event.target.value;
    value = value.substring(0, 60);
    value = value.replace(/[^A-Za-z\s\.\,\-\&\'\(\)\/]/g, '');
    value = this.preventBackToBackChars(value);
    this.eppRegistrationForm.get('department')?.setValue(value, { emitEvent: false });
    this.checkForSpecialCharAtEnd('department');
  }

  onTitleInput(event: any) {
    let value = event.target.value;
    value = value.substring(0, 60);
    value = value.replace(/[^A-Za-z\s\.\,\-\&\'\(\)\/]/g, '');
    value = this.preventBackToBackChars(value);
    this.eppRegistrationForm.get('title')?.setValue(value, { emitEvent: false });
    this.checkForSpecialCharAtEnd('title');
  }

  onStreetAddressInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^A-Za-z0-9\s\.\,\-\'\#\/]/g, '');
    value = value.substring(0, 60);
    value = this.preventBackToBackChars(value);
    this.eppRegistrationForm.get('streetAddress')?.setValue(value, { emitEvent: false });
    this.checkForSpecialCharAtEnd('streetAddress');
  }

  onExtensionInput(event: any) {
    let value = event.target.value;
    value = value.replace(/[^0-9]/g, '');
    value = value.substring(0, 10);
    this.eppRegistrationForm.get('extension')?.setValue(value, { emitEvent: false });
  }

  onFieldBlur(event: any, controlName: string) {
    let value = event.target.value;
    value = value.trim();
    this.eppRegistrationForm.get(controlName)?.setValue(value, { emitEvent: false });
    this.checkForSpecialCharAtEnd(controlName);
  }

  checkForSpecialCharAtEnd(controlName: string) {
    const fieldsToCheck = ['organization', 'title', 'department', 'streetAddress'];
    if (!fieldsToCheck.includes(controlName)) return;

    const value = this.eppRegistrationForm.get(controlName)?.value;
    if (!value) return;

    let specialCharsPattern;
    if (controlName === 'streetAddress') {
      specialCharsPattern = /[.,\-'#\/]$/;
    } else {
      specialCharsPattern = /[.,\-&'()\/]$/;
    }

    const endsWithSpecialChar = specialCharsPattern.test(value);

    if (endsWithSpecialChar) {
      this.eppRegistrationForm.get(controlName)?.setErrors({
        ...this.eppRegistrationForm.get(controlName)?.errors,
        specialCharAtEnd: true
      });
    } else {
      const errors = this.eppRegistrationForm.get(controlName)?.errors;
      if (errors && errors['specialCharAtEnd']) {
        delete errors['specialCharAtEnd'];
        if (Object.keys(errors).length === 0) {
          this.eppRegistrationForm.get(controlName)?.setErrors(null);
        } else {
          this.eppRegistrationForm.get(controlName)?.setErrors(errors);
        }
      }
    }
  }

  checkForTrailingSpace(event: any, controlName: string) {
    let value = event.target.value;
    this.eppRegistrationForm.get(controlName)?.setValue(value.trimEnd(), { emitEvent: false });
  }

  normalizeSpaces(value: string) {
    value = value.replace(/\s+/g, ' ');
    return value;
  }

  preventBackToBackChars(value: string): string {
    value = value.replace(/^\s+/, '');
    value = value.replace(/\s{2,}/g, ' ');
    value = value.replace(/[.]{2,}/g, '.');
    value = value.replace(/[,]{2,}/g, ',');
    value = value.replace(/[-]{2,}/g, '-');
    value = value.replace(/[&]{2,}/g, '&');
    value = value.replace(/[']{2,}/g, "'");
    value = value.replace(/[\(]{2,}/g, '(');
    value = value.replace(/[\)]{2,}/g, ')');
    value = value.replace(/[\/]{2,}/g, '/');
    value = value.replace(/[#]{2,}/g, '#');

    value = value.replace(/([.,\-&'()\/#])\1+/g, '$1');

    while (/[.,\-&'()\/#][.,\-&'()\/#]/.test(value)) {
      value = value.replace(/([.,\-&'()\/#])([.,\-&'()\/#])/, '$1');
    }

    return value;
  }

  // Phone number formatting
  formatPhoneNumber(event: any) {
    let input = event.target.value;
    const isDeleting = event.inputType === 'deleteContentBackward';

    if (/[a-zA-Z]/.test(input)) {
      input = input.replace(/[a-zA-Z]/g, '');
    }

    //Auto prepend +1 only when typing (not deleting)
    if (!isDeleting && !input.startsWith('+1') && input !== '+') {
      input = '+1 ' + input.replace(/^\+/, '');
    }

    if (!input) {
      this.eppRegistrationForm.get('phoneNumber')?.setValue('', { emitEvent: false });
      return;
    }

    if (input.startsWith('+1')) {
      if (input.length > 2 && input[2] !== ' ') {
        if (event.inputType !== 'deleteContentBackward') {
          input = '+1 ' + input.slice(2);
        }
      }
      let numberPart = input.startsWith('+1 ') ? input.slice(3) : input.slice(2);
      let digits = numberPart.replace(/\D/g, '');
      // Strip repeated country code: e.g. user pasted 18XXXXXXXXX after field already had "+1 "
      if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.substring(1);
      }
      if (digits.length > 10) digits = digits.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      let finalValue = input.startsWith('+1 ') ? '+1 ' + formatted : '+1' + formatted;
      this.eppRegistrationForm.get('phoneNumber')?.setValue(finalValue, { emitEvent: false });
      return;
    }

    if (input.startsWith('(')) {
      let digits = input.replace(/\D/g, '');
      if (digits.length > 10) digits = digits.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      this.eppRegistrationForm.get('phoneNumber')?.setValue(formatted, { emitEvent: false });
      return;
    }

    if (/^\d+$/.test(input)) {
      let digits = input.substring(0, 10);
      let formatted = digits;
      if (digits.length > 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
      } else if (digits.length > 3) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
      } else if (digits.length > 0) {
        formatted = `(${digits}`;
      } else {
        formatted = '';
      }
      this.eppRegistrationForm.get('phoneNumber')?.setValue(formatted, { emitEvent: false });
      return;
    }

    if (input === '+' || input === '+1' || input === '+1 ') {
      this.eppRegistrationForm.get('phoneNumber')?.setValue(input, { emitEvent: false });
      return;
    }

    this.eppRegistrationForm.get('phoneNumber')?.setValue('', { emitEvent: false });
    return;
  }

  checkPhone() {
    const phoneNumberValue = this.eppRegistrationForm.get('phoneNumber')?.value;
    if (!phoneNumberValue || phoneNumberValue.trim() === '') {
      this.phoneError = false;
      this.phoneExists = false;
      return;
    }

    let cleanedPhoneNumber = phoneNumberValue.replace(/\D/g, '');

    if (cleanedPhoneNumber.length === 11 && cleanedPhoneNumber.startsWith('1')) {
      cleanedPhoneNumber = cleanedPhoneNumber.substring(1);
    }

    if (cleanedPhoneNumber.length === 10 && /^0+$/.test(cleanedPhoneNumber)) {
      this.phoneError = false;
      this.phoneExists = false;
      return;
    }

    if (cleanedPhoneNumber.length !== 10) {
      this.phoneError = false;
      this.phoneExists = false;
      return;
    }

    let request = {
      phone: cleanedPhoneNumber
    };

    this.registrationService.checkEppPhoneNumber(request).subscribe({
      next: response => {
        if (response.data?.data && response.data.data.length > 0 && response.data.data[0].phone_exists === true) {
          this.closeConfirmModal();
          window.scrollTo(0, 0);
          this.phoneError = true;
          this.phoneExists = true;
          setTimeout(() => {
            this.phoneError = false;
          }, 5000);
        } else {
          this.phoneError = false;
          this.phoneExists = false;
        }
      },
      error: () => {
        this.phoneError = false;
        this.phoneExists = false;
      }
    });
  }

  closeErrorToast() {
    this.phoneError = false;
  }

  // Zip code formatting
  cityStateRequiredIfZipValid(form: FormGroup) {
    const zip = form.get('zipCode')?.value || '';
    const city = form.get('city')?.value || '';
    const state = form.get('state')?.value || '';
    const zipPattern = /^\d{5}$|^\d{5}-\d{4}$/;

    if (zipPattern.test(zip) && (!city || !state)) {
      return { cityStateMissing: true };
    }
    return null;
  }

  zipCodeFormat(event: any) {
    console.log("zipcode:🤳", event.target.value)
    let value = event.target.value.replace(/\D/g, '');
    if (value.length > 10) value = value.substring(0, 10);
    let formatted = value;

    if (value.length > 5 && value.length < 9) {
      this.eppRegistrationForm.get('zipCode')?.setErrors({ 'invalidZipLength': true });
    }

    if (value.length > 5) {
      formatted = ` ${value.substring(0, 5)}-${value.substring(5)}`;
    }

    if (value.length >= 5) {
      const allZerosPattern = /^0+$/;
      const isAllZeros = allZerosPattern.test(value);
      if (isAllZeros) {
        const inputType = event.inputType || '';
        if (inputType !== 'deleteContentBackward' && inputType !== 'deleteContentForward') {
          let masked = value.length === 5 ? '00000' : '00000-0000';
          this.eppRegistrationForm.get('zipCode')?.setValue(masked, { emitEvent: false });
        }
        this.eppRegistrationForm.get('zipCode')?.setErrors({ 'allZeros': true });
        return;
      }
    }

    this.eppRegistrationForm.get('zipCode')?.setValue(formatted.trim(), { emitEvent: false });

    if (value.length < 5 || (value.length > 5 && value.length < 9)) {
      this.eppRegistrationForm.patchValue({
        city: '',
        state: ''
      });
    }

    if (value.length === 5 || value.length === 9) {
      setTimeout(() => {
        this.getAddress();
      }, 100);
    }
  }

  getAddress() {
    const zipCodeControl = this.eppRegistrationForm.get('zipCode');
    const zipCodeValue = zipCodeControl?.value;

    if (zipCodeControl?.invalid) {
      console.log('Zip code has validation errors, not fetching address:', zipCodeControl.errors);
      this.eppRegistrationForm.patchValue({
        city: '',
        state: ''
      });
      return;
    }

    if (!zipCodeValue || zipCodeValue.trim() === '') {
      console.log('Zip code is empty');
      return;
    }

    const cleanZipCode = zipCodeValue.replace(/\D/g, '');
    if (cleanZipCode.length !== 5 && cleanZipCode.length !== 9) {
      console.log('Invalid zip code length:', cleanZipCode.length);
      this.eppRegistrationForm.patchValue({
        city: '',
        state: ''
      });
      return;
    }

    console.log('Calling smarty validation for valid zip code:', zipCodeValue);

    let input = {
      smartyInd: false,
      zipcode: this.eppRegistrationForm.get('zipCode')?.value
    }

    this.registrationService.smartyValidation(input).subscribe({
      next: response => {
        if(response.status === 'success'){
          if(response.data.length>0){
            console.log('smartyInd value:', input.smartyInd);
            console.log('Response data:', response.data[0].zipcodes[0]);
            const zipData = response.data[0].zipcodes[0];

            this.eppRegistrationForm.patchValue({
              city: zipData.default_city,
              state: zipData.state
            });
          }
        }
      }
    });
  }

  // Form submission
  onSubmit() {
    if (this.eppRegistrationForm.valid) {
      const rawValues = this.eppRegistrationForm.getRawValue();
      const formData = {
        email: rawValues.email,
        first_name: rawValues.firstName,
        last_name: rawValues.lastName,
        department: rawValues.department,
        title: rawValues.title,
        cd_epp_provider: rawValues.eppProvider,
        street_address: rawValues.streetAddress,
        zipcode: rawValues.zipCode,
        city: rawValues.city,
        state: rawValues.state,
        phone: rawValues.phoneNumber.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1'),
        extensions: rawValues.extension,
        is_channel: rawValues.terms,
        is_consent: rawValues.consent,
      };
      this.localstorage.setItem('externalRegistrationFormData', JSON.stringify(formData))

       this.registrationService
                .addExternalRegisterDetails(formData, 'epp')
                .pipe(
                  take(1),
                  takeUntil(this.destroy$),
                  finalize(() => {
                    // Ensures we stop loading regardless of success/error/early returns
                    // this.isLoading = false;
                  })
                )
                .subscribe({
                  next: (response: any) => {
                    this.alertService.success(
  `Your password has been set successfully.\nPlease log in with your new credentials to continue.`);
                    setTimeout(() => {
                       this.router.navigate(['/login']);
                    }, 5000);  
                   
                     },
            error: (error) => {
              // Network/server errors or thrown errors
              const msg =
                error?.error?.message ||
                error?.message ||
                'Something went wrong while creating your account.';
              this.alertService.error(msg);
              console.error('Registration API error:', error);
            }
          });
    } else {
      this.eppRegistrationForm.markAllAsTouched();
      Object.keys(this.eppRegistrationForm.controls).forEach(key => {
        const control = this.eppRegistrationForm.get(key);
        if (control && control.invalid) {
          console.log(`Field ${key} is invalid:`, control.errors);
        }
      });
    }
  }

  goToLogin() {
    this.router.navigate(['']);
  }

  // Validators
  validateDate(control: any) {
    const selectedDate = new Date(control.value);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate > today) {
      return { 'futureDate': true };
    }

    return null;
  }

  // Getters
  get firstName() {
    return this.eppRegistrationForm.get('firstName');
  }

  get lastName() {
    return this.eppRegistrationForm.get('lastName');
  }

  get email() {
    return this.eppRegistrationForm.get('email');
  }

  get phoneNumber() {
    return this.eppRegistrationForm.get('phoneNumber');
  }

  get terms() {
    return this.eppRegistrationForm.get('terms');
  }

  get department() {
    return this.eppRegistrationForm.get('department');
  }

  get title() {
    return this.eppRegistrationForm.get('title');
  }

  get streetAddress() {
    return this.eppRegistrationForm.get('streetAddress');
  }

  get zipCode() {
    return this.eppRegistrationForm.get('zipCode');
  }

  get city() {
    return this.eppRegistrationForm.get('city');
  }

  get state() {
    return this.eppRegistrationForm.get('state');
  }

  get extension() {
    return this.eppRegistrationForm.get('extension');
  }

  // Modal handlers
  open(content) {
    this.ngModal.open(content, { size: 'md', backdrop: 'static' }).result.then(
      result => {
        this.closeResult = `Closed with: ${result}`;
      },
      reason => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }

  private getDismissReason(reason: any): string {
    if (reason === ModalDismissReasons.ESC) {
      return 'by pressing ESC';
    } else if (reason === ModalDismissReasons.BACKDROP_CLICK) {
      return 'by clicking on a backdrop';
    } else {
      return `with: ${reason}`;
    }
  }

  private closeConfirmModal() {
    if (this.modalRef) {
      this.modalRef.close();
    }
  }

  showConfirmRegModal(): void {
    this.showModal = true;
    setTimeout(() => {
      this.router.navigate(['/login']);
    }, 2000);
    setTimeout(() => {
      this.closeModal();
    }, 6000);
  }

  closeModal(): void {
    this.showModal = false;
  }

  openTermsAndConditionsModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.userAgreementContent) {
      this.showTermsAndConditionsModal();
      return;
    }

    
    this.registrationService.getUserAgreementContent(500, '3', 'EPP').subscribe({
      next: (response: any) => {
        if (response?.data?.length > 0) {
          const optionalData = response.data[0].optionaldata;
          if (optionalData) {
            try {
              const parsed = typeof optionalData === 'string' ? JSON.parse(optionalData) : optionalData;
              this.userAgreementContent = parsed?.content || '';
            } catch {
              this.userAgreementContent = optionalData;
            }
          }
        }
        this.showTermsAndConditionsModal();
      },
      error: err => {
        console.error('Error loading user agreement content:', err);
        this.showTermsAndConditionsModal();
      }
    });
  }

  private showTermsAndConditionsModal(): void {
    this.matDialog.open(ContentManagementModalComponent, {
      width: '900px',
      disableClose: true,
      data: {
        heading: modalHeading.useragreementepp,
        content: this.userAgreementContent,
        buttons: [{ text: modalButtonTitles.close, class: 'btn-outline-primary' }]
      }
    });
  }

  openPrivacyPolicyModal(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    const options: NgbModalOptions = {
      backdrop: "static",
      centered: true,
      size: "lg"
    };

    this.customModal = this.ngModal.open(ModalComponent, options);

    const modalConfig: ModalConfig = {
      buttons: [
        { text: modalButtonTitles.close, code: 'N', btnOutline: false }
      ],
      heading: modalHeading.privacyPolicy,
      modalContent: modalContent.privacyPolicy,
      hasTemplate: true,
      scrollable: true
    };

    this.customModal.componentInstance.modalConfig = modalConfig;
  }
}
